import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import type { IntuiTypographyProps, TypographyVariant } from './types'

/**
 * Figma MCP — Caliper Design System Typography
 *
 * H1 (28:1469): text-5xl (48px), font-extrabold (800), leading-[48px]
 * H2 (26:216): text-3xl (30px), font-semibold (600), leading-9 (36px), border-b pb-2
 * H3: text-2xl, font-semibold, tracking-tight
 * H4: text-xl, font-semibold, tracking-tight
 * P (26:239): text-base (16px), font-normal (400), leading-6 (24px)
 * Blockquote (26:245): border-l-2 pl-6 pt-6, text-base, leading-6, NOT italic
 * Lead (26:314): text-xl (20px), font-normal, leading-7 (28px), text-muted-foreground
 * InlineCode (37:58): bg-muted, rounded-sm (6px), px-[4.8px] py-[3.2px], font-mono, text-sm, font-normal
 */
const typographyVariants = cva('text-foreground', {
  variants: {
    variant: {
      h1: 'scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl lg:leading-[48px]',
      h2: 'scroll-m-20 border-b border-border pb-2 text-3xl font-semibold leading-9 tracking-tight first:mt-0',
      h3: 'scroll-m-20 text-2xl font-semibold tracking-tight',
      h4: 'scroll-m-20 text-xl font-semibold tracking-tight',
      p: 'text-base leading-6 [&:not(:first-child)]:mt-6',
      blockquote: 'mt-6 border-l-2 border-border pl-6 text-base leading-6 text-foreground',
      lead: 'text-xl leading-7 text-muted-foreground',
      large: 'text-lg font-semibold',
      small: 'text-sm font-medium leading-none',
      muted: 'text-sm text-muted-foreground',
      inlineCode:
        'relative rounded-sm bg-muted px-[4.8px] py-[3.2px] font-mono text-sm font-normal',
    },
  },
  defaultVariants: {
    variant: 'p',
  },
})

/** Default HTML element for each variant */
const defaultElements: Record<TypographyVariant, React.ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  p: 'p',
  blockquote: 'blockquote',
  lead: 'p',
  large: 'div',
  small: 'small',
  muted: 'p',
  inlineCode: 'code',
}

function IntuiTypography({
  variant,
  children,
  className,
  as,
}: IntuiTypographyProps) {
  const Component = as ?? defaultElements[variant]

  return (
    <Component className={cn(typographyVariants({ variant }), className)}>
      {children}
    </Component>
  )
}

export { IntuiTypography, typographyVariants }
